import { useDebounceFn } from '@vueuse/core'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { usePromocodesStore } from './store'
import { usePromocodesApi } from '../api/list.hook'
import { toDiscountOptions, usePromocodeOptions } from './promocode-options'

const SEARCH_DEBOUNCE_MS = 400

interface FiltersPromocodesReturn {
  refSearch: WritableComputedRef<string>
  refStatus: WritableComputedRef<string | undefined>
  refDiscount: WritableComputedRef<number | undefined>
  refDateRange: WritableComputedRef<Date[] | null>
  statusOptions: ComputedRef<AppSelectOption[]>
  discountOptions: ComputedRef<AppSelectOption<number>[]>
  canManage: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
  openCreateModal: () => void
}

export const useFiltersPromocodes = (): FiltersPromocodesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = usePromocodesStore()
  const dayjs = useDayjs()
  const { statusOptions } = usePromocodeOptions()
  const { canManagePromocodes } = useSidebarPrivileges()

  const discountOptions = computed<AppSelectOption<number>[]>(() => toDiscountOptions(store.getDiscounts))

  const syncQueryAndReload = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        q: store.getFilterSearch || undefined,
        status: store.getFilterStatus || undefined,
        discount: store.getFilterDiscount ? String(store.getFilterDiscount) : undefined,
        from: store.getFilterFrom || undefined,
        to: store.getFilterTo || undefined,
        page: undefined,
      },
    })
    await usePromocodesApi()
  }

  const applySearch = useDebounceFn(() => syncQueryAndReload(), SEARCH_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => {
      store.setFilterSearch(value.trim() || null)
      void applySearch()
    },
  })

  const refStatus = computed<string | undefined>({
    get: () => store.getFilterStatus ?? undefined,
    set: (value) => {
      store.setFilterStatus(value ?? null)
      void syncQueryAndReload()
    },
  })

  const refDiscount = computed<number | undefined>({
    get: () => store.getFilterDiscount ?? undefined,
    set: (value) => {
      store.setFilterDiscount(value ?? null)
      void syncQueryAndReload()
    },
  })

  const refDateRange = computed<Date[] | null>({
    get: () => {
      const from = store.getFilterFrom
      const to = store.getFilterTo
      if (!from || !to) return null

      const fromDate = dayjs(from)
      const toDate = dayjs(to)

      return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
    },
    set: (value) => {
      if (Array.isArray(value) && value.length === 2) {
        store.setFilterFrom(dayjs(value[0]).format(DateFormat.Default))
        store.setFilterTo(dayjs(value[1]).format(DateFormat.Default))
      } else {
        store.setFilterFrom(null)
        store.setFilterTo(null)
      }

      void syncQueryAndReload()
    },
  })

  const canManage = computed<boolean>(() => canManagePromocodes.value)
  const isLoading = computed<boolean>(() => store.isLoading)

  const clearFilters = (): void => {
    store.resetFilters()
    void syncQueryAndReload()
  }

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  return {
    refSearch,
    refStatus,
    refDiscount,
    refDateRange,
    statusOptions,
    discountOptions,
    canManage,
    isLoading,
    clearFilters,
    openCreateModal,
  }
}
